import React, { useState, useEffect, useCallback, useRef } from 'react';

interface EmailCollectionModalProps {
  onSubmit: (email: string) => void;
  onSkip: () => void;
}

// --- Tailwind class constants (旧 styled-components) ---

const OVERLAY =
  'fixed top-0 left-0 right-0 bottom-0 bg-black/50 backdrop-blur-[8px] flex items-center justify-center z-[1100] p-5 animate-[obc-fade-in_0.3s_ease-out]';

const MODAL_CONTAINER =
  'bg-white rounded-2xl w-full max-w-[440px] py-10 px-8 shadow-[0_20px_60px_rgba(0,0,0,0.3)] animate-[obc-fade-in-up-20_0.4s_ease-out] text-center';

const ENVELOPE_ICON =
  'w-16 h-16 mx-auto mb-6 flex items-center justify-center [&_svg]:w-12 [&_svg]:h-12';

const TITLE = 'text-[1.375rem] font-bold text-gray-800 m-0 mb-2';

const SUBTITLE = 'text-[0.9375rem] text-gray-500 m-0 mb-6 leading-[1.5]';

const FORM = 'w-full';

const EMAIL_INPUT_BASE =
  'w-full py-[14px] px-4 text-[1rem] rounded-lg border-2 bg-white text-gray-800 transition-[border-color,box-shadow] duration-200 ease-[ease] outline-none placeholder:text-gray-400';
const EMAIL_INPUT_ERROR =
  'border-red-600 focus:border-red-600 focus:shadow-[0_0_0_3px_rgba(220,38,38,0.2)]';
const EMAIL_INPUT_OK =
  'border-gray-300 focus:border-teal-500 focus:shadow-[0_0_0_3px_rgba(20,184,166,0.2)]';

const ERROR_TEXT = 'mt-1.5 text-[0.8125rem] text-red-600 text-left';

const SUBMIT_BUTTON_BASE =
  'w-full py-[14px] px-6 text-[1.0625rem] font-semibold text-white border-0 rounded-lg transition-all duration-200 ease-[ease] mt-4 enabled:hover:bg-teal-600 enabled:hover:-translate-y-px enabled:hover:shadow-[0_4px_12px_rgba(20,184,166,0.3)] enabled:active:translate-y-0 enabled:active:shadow-none';
const SUBMIT_BUTTON_DISABLED = 'bg-gray-300 cursor-not-allowed';
const SUBMIT_BUTTON_ENABLED = 'bg-teal-500 cursor-pointer';

const INFO_TEXT = 'text-[0.75rem] text-gray-400 mt-3 leading-[1.4]';

const SKIP_LINK =
  'bg-transparent border-0 text-gray-400 text-[0.8125rem] cursor-pointer p-2 mt-4 transition-[color] duration-200 ease-[ease] hover:text-gray-500 hover:underline';

const EnvelopeSvg: React.FC = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="10" width="40" height="28" rx="4" fill="#E0F2FE" stroke="#14b8a6" strokeWidth="2" />
    <path d="M4 14L24 28L44 14" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 38L18 26" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
    <path d="M44 38L30 26" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const EmailCollectionModal: React.FC<EmailCollectionModalProps> = ({
  onSubmit,
  onSkip,
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();
  const onSkipRef = useRef(onSkip);

  // Keep onSkip ref up to date
  useEffect(() => {
    onSkipRef.current = onSkip;
  }, [onSkip]);

  // Escape key handler + body scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onSkipRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, []);

  const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onSkipRef.current();
    }
  }, []);

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setError('メールアドレスを入力してください');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setError('有効なメールアドレスを入力してください');
      return false;
    }

    setError(undefined);
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateEmail(email)) {
      onSubmit(email.trim());
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) {
      setError(undefined);
    }
  };

  const handleBlur = () => {
    if (email.trim()) {
      validateEmail(email);
    }
  };

  const isDisabled = !email.trim();

  return (
    <div className={OVERLAY} onClick={handleOverlayClick}>
      <div className={MODAL_CONTAINER}>
        <div className={ENVELOPE_ICON}>
          <EnvelopeSvg />
        </div>

        <h2 className={TITLE}>バナーをメールでお届けします</h2>
        <p className={SUBTITLE}>高解像度版のバナーをメールで受け取れます</p>

        <form className={FORM} onSubmit={handleSubmit}>
          <input
            className={`${EMAIL_INPUT_BASE} ${error ? EMAIL_INPUT_ERROR : EMAIL_INPUT_OK}`}
            type="email"
            value={email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="example@company.com"
            autoFocus
            autoComplete="email"
          />
          {error && <div className={ERROR_TEXT}>{error}</div>}

          <button
            className={`${SUBMIT_BUTTON_BASE} ${isDisabled ? SUBMIT_BUTTON_DISABLED : SUBMIT_BUTTON_ENABLED}`}
            type="submit"
            disabled={isDisabled}
          >
            バナーを受け取る
          </button>
        </form>

        <p className={INFO_TEXT}>
          登録するとTaskyの最新情報もお届けします
        </p>

        <button className={SKIP_LINK} type="button" onClick={onSkip}>
          スキップ
        </button>
      </div>
    </div>
  );
};

export default EmailCollectionModal;
